<template lang="html">
  <div v-show="!statistic['_destroy']" class="statistic-field-row">
    <div class="statistic-field-main">
      <combo-box
        v-model="metricTypeLookup"
        class="statistic-field-name"
        :options="labelOptions"
        :allow-custom="allowAdditions"
        placeholder="Search or type a new metric"
        @blur="save"
      />

      <select
        v-show="isUserEntered"
        v-model="statistic.metric_type"
        class="statistic-field-type"
        @change="save"
      >
        <option v-for="option in metricTypes" :key="option" :value="option">
          {{ option }}
        </option>
      </select>

      <div v-if="isTimeMetric" class="statistic-field-duration">
        <input
          v-model.number="durationHours"
          type="number"
          min="0"
          placeholder="0"
          @input="onUserEditValue"
          @blur="save"
          @keydown.enter.prevent="$event.target.blur()"
        />
        <span class="statistic-field-duration-unit">h</span>
        <input
          v-model.number="durationMinutes"
          type="number"
          min="0"
          max="59"
          placeholder="0"
          @input="onUserEditValue"
          @blur="save"
          @keydown.enter.prevent="$event.target.blur()"
        />
        <span class="statistic-field-duration-unit">m</span>
      </div>
      <!--
        Enter blurs the field, which saves it — and MUST NOT also submit the
        form. The deliverable form is a real <form @submit.stop.prevent="save">,
        so implicit submission fired its whole-deliverable PATCH (which writes
        the same row again through statistics_attributes) in the same
        millisecond as this field's own POST, producing two identical manual
        rows. keydown, not keyup: implicit submission happens on keydown, so a
        keyup handler runs after the form has already gone.
      -->
      <input
        v-else
        v-model="statistic.value"
        class="statistic-field-value"
        :type="inputTypeFromMetricType"
        placeholder="Value"
        @input="onUserEditValue"
        @blur="save"
        @keydown.enter.prevent="$event.target.blur()"
      />

      <span v-if="saving" class="ui active mini inline loader" />
      <a
        v-else
        class="statistic-field-remove"
        title="Remove"
        @click.stop.prevent="remove"
      >
        <i class="icon times fitted" />
      </a>
    </div>

    <div
      v-if="statistic.estimated"
      class="estimated-stat-note"
      title="Estimated from this publication's traffic rank. Type a value to override."
    >
      <i class="icon info circle fitted" /> Estimated &mdash; edit to override
    </div>
  </div>
</template>

<script>
import ComboBox from "components/comboBox.vue";
import { createSingleFlightSave } from "lib/singleFlightSave.mjs";

export default {
  components: {
    ComboBox,
  },
  props: {
    value: {
      type: Object,
      default: function () {
        return {};
      },
    },
    metricTypes: {
      type: Array,
    },
    // [{label, metric_type}] already fetched onto this deliverable.
    existingLabels: {
      type: Array,
      default() {
        return [];
      },
    },
    allowAdditions: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const $this = this;
    return {
      statistic: $this.value,
      saving: false,
    };
  },

  computed: {
    isUserEntered() {
      return (
        this.metricTypeLookup != null &&
        !Object.keys(this.knownMetricTypes).includes(this.metricTypeLookup)
      );
    },
    // Labels already on this deliverable, first and in the order the server
    // reported them, then the generic options alphabetically. A custom stat
    // only overrides an auto-fetched one when the labels match EXACTLY, and
    // nobody can be expected to guess that TikTok's views row is spelled
    // "Play Count" — so the row you might want to correct is the first thing
    // the list offers. See Deliverable#existing_stat_labels.
    existingLabelNames() {
      const seen = new Set();
      return this.existingLabels
        .map((s) => s && s.label)
        .filter((label) => {
          if (!label || seen.has(label)) return false;
          seen.add(label);
          return true;
        });
    },
    labelOptions() {
      const existing = this.existingLabelNames;
      const rest = Object.keys(this.metricTypeDictionary)
        .filter((label) => !existing.includes(label))
        .sort();
      return [...existing, ...rest];
    },
    // Options with a known metric type — the generic list plus this
    // deliverable's own rows. Picking one of these carries the right
    // metric_type across, so the type <select> stays hidden; it only appears
    // for a label the user typed themselves.
    knownMetricTypes() {
      let hash = { ...this.defaultMetricTypes };
      this.existingLabels.forEach((s) => {
        if (s && s.label && s.metric_type) hash[s.label] = s.metric_type;
      });
      return hash;
    },
    metricTypeLookup: {
      get() {
        return this.statistic && this.statistic.label;
      },
      set(newVal) {
        if (!newVal) {
          return;
        } else if (this.metricTypeDictionary[newVal.trim()]) {
          this.$set(
            this.statistic,
            "metric_type",
            this.metricTypeDictionary[newVal]
          );
          this.$set(this.statistic, "label", newVal);
        } else {
          this.$set(this.statistic, "metric_type", "other");
          this.$set(this.statistic, "label", newVal);
        }
      },
    },
    defaultMetricTypes() {
      let hash = {
        "Link Clicks": "engagements",
        "Sticker Taps": "engagements",
        "Swipe Ups": "engagements",
        Comments: "engagements",
        Impressions: "impressions",
        Likes: "engagements",
        Reach: "impressions_unique",
        Replies: "engagements",
        Saves: "engagements",
        Shares: "engagements",
        Views: "views",
        "Paid Impressions": "impressions",
        "Paid Reach": "impressions_unique",
        "Paid Views": "views",
      };

      this.metricTypes.map((type) => {
        hash[this.$options.filters.humanize(type)] = type;
      });
      return hash;
    },
    metricTypeDictionary() {
      let hash = { ...this.knownMetricTypes };
      if (
        this.statistic &&
        this.statistic.label &&
        this.statistic.metric_type
      ) {
        hash[this.statistic.label] = this.statistic.metric_type;
      }

      return hash;
    },
    inputTypeFromMetricType() {
      switch (this.statistic.metric_type) {
        case "impressions":
        case "engagements":
        case "shares":
        case "views":
        case "impressions_unique":
          return "number";
        default:
          return "text";
      }
    },
    // time_spent/avg_time_spent are stored (and displayed on the card) in
    // seconds, but a bare "Value" field asking someone to enter, say, 99990
    // for "27h 46m" is not a thing anyone can do in their head. Swap in an
    // Hours/Minutes pair that reads/writes the same underlying seconds value.
    isTimeMetric() {
      return (
        this.statistic.metric_type === "time_spent" ||
        this.statistic.metric_type === "avg_time_spent"
      );
    },
    durationHours: {
      get() {
        return Math.floor((parseInt(this.statistic.value, 10) || 0) / 3600);
      },
      set(hours) {
        const h = parseInt(hours, 10) || 0;
        this.statistic.value = String(h * 3600 + this.durationMinutes * 60);
      },
    },
    durationMinutes: {
      get() {
        return Math.floor(((parseInt(this.statistic.value, 10) || 0) % 3600) / 60);
      },
      set(minutes) {
        const m = parseInt(minutes, 10) || 0;
        this.statistic.value = String(this.durationHours * 3600 + m * 60);
      },
    },
    endpoint() {
      return "/statistics";
    },
    url() {
      let url = `${this.endpoint}`;
      if (this.statistic.id) {
        url += `/${this.statistic.id}`;
      }
      return url;
    },
    requestType() {
      let type = `post`;
      if (this.statistic.id) {
        type = `patch`;
      }
      return type;
    },
  },
  watch: {
    value(newVal) {
      this.statistic = newVal;
    },
  },
  created() {
    // Deliberately not in data(): this is plain control state, and `this.saving`
    // is the reactive mirror the spinner reads. One guard per field row, so two
    // rows still save independently.
    this.saveFlight = createSingleFlightSave((done) => this.performSave(done));
  },
  mounted() {
    // Blank/user rows are manual entries. An estimate is explicitly not a
    // manual value (and must never feed the calibration model), so leave its
    // flags as set by the suggestion. This applies equally to a SAVED estimate
    // loaded from the server (the deliverable payload now includes estimated
    // rows): it stays manual_update:false and keeps rendering the "Estimated"
    // note until the user actually edits the value.
    if (!this.statistic.estimated) {
      this.statistic.manual_update = true;
    }
  },
  methods: {
    // Fires only on real user keystrokes (not programmatic v-model writes). A
    // user touching the value takes ownership: it becomes a manual entry, no
    // longer an estimate. Clearing an estimate counts as dismissing it so it
    // won't be re-suggested.
    onUserEditValue() {
      if (!this.statistic.estimated) return;
      if (this.statistic.value === "" || this.statistic.value == null) {
        this.$emit("estimate-dismissed", this.statistic);
      }
      // The user took ownership: this is now a confirmed manual value, which
      // outranks both estimated and auto-fetched rows for the same label. (An
      // untouched suggestion still saves — as an estimate. It used to carry a
      // `suggested` flag that the server dropped silently, so the number showed
      // on screen and never reached the report; the client no longer sends it.)
      this.$set(this.statistic, "estimated", false);
      this.$set(this.statistic, "manual_update", true);
    },
    remove() {
      const $this = this;
      // Never saved — nothing to delete server-side, and no data loss to
      // confirm. Just drop the draft row.
      if (!this.statistic.id) {
        $this.$emit("remove", $this.statistic);
        return;
      }
      if (window.confirm("Are you sure you want to delete this statistic?")) {
        window.$.ajax({
          url: $this.url,
          type: "delete",
          dataType: "json",
          data: {
            id: $this.statistic.id,
          },
          success: function (data) {
            if ($this.statistic.estimated) {
              $this.$emit("estimate-dismissed", $this.statistic);
            }
            $this.$emit("remove", $this.statistic);
          },
        });
      }
    },
    // One request at a time, and the last edit always lands. `requestType` is
    // derived from statistic.id, which is only assigned in the success callback
    // — so two overlapping saves of a new row both POST and create two
    // identical statistics. Both the combo box and the value input save on blur
    // (and Enter blurs the value input), so the ordinary fill-the-label-then-
    // type-the-value interaction fires save twice before the first response
    // lands. See lib/singleFlightSave.mjs for the queue-and-replay rules.
    save: function () {
      const $this = this;
      if (!this.statistic.metric_type || !this.statistic.value) {
        return;
      }

      // An untouched estimate stays an estimate (labeled, and excluded from
      // the traffic-model calibration). Anything else is a manual entry.
      if (!this.statistic.estimated) {
        this.statistic.manual_update = true;
      }

      this.saveFlight.run();
    },
    // The request itself. `done` releases the single-flight guard and replays
    // any edit that arrived mid-flight; it must be called on failure too, or
    // the field stays locked.
    performSave: function (done) {
      const $this = this;

      $this.saving = true;
      window.$.ajax({
        url: $this.url,
        type: $this.requestType,
        dataType: "json",
        data: {
          statistic: $this.statistic,
        },
        success: function (data) {
          $this.$set($this.statistic, "id", data.id);
          $this.$emit("input", $this.statistic);
          $this.$emit("change", $this.statistic);
        },
        // `complete`, not `success`: a failed request has to stop the spinner
        // and release the guard, or the row can never be saved again.
        complete: function () {
          $this.saving = false;
          done();
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.statistic-field-row {
  padding: 0.3rem 0;
}

.statistic-field-main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.statistic-field-name {
  flex: 1;
  min-width: 0;
}

.statistic-field-type {
  flex: 0 0 auto !important;
  max-width: 8rem !important;
  border: none !important;
  border-bottom: 1px solid var(--border-light, #e2e2e2) !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0.25rem 0.1rem !important;
  font-size: 0.85rem;
  color: var(--text-muted, #787a92);

  &:focus {
    outline: none;
    border-bottom-color: #f4806f !important;
  }
}

.statistic-field-value {
  flex: 0 0 auto !important;
  width: 5.5rem !important;
  border: none !important;
  border-bottom: 1px solid var(--border-light, #e2e2e2) !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  text-align: right;
  padding: 0.25rem 0.1rem !important;
  font-size: 0.92rem;
  color: var(--text-primary, #1a202c);

  &:focus {
    outline: none;
    border-bottom-color: #f4806f !important;
  }

  &::placeholder {
    color: var(--text-muted, #a0aec0);
  }
}

.statistic-field-duration {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 0.2rem;
  border-bottom: 1px solid var(--border-light, #e2e2e2);

  input {
    width: 2.5rem;
    border: none !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    text-align: right;
    padding: 0.25rem 0.1rem !important;
    font-size: 0.92rem;
    color: var(--text-primary, #1a202c);

    &:focus {
      outline: none;
    }
  }

  &:focus-within {
    border-bottom-color: #f4806f;
  }
}

.statistic-field-duration-unit {
  color: var(--text-muted, #a0aec0);
  font-size: 0.85rem;

  &:last-child {
    margin-right: 0.15rem;
  }
}

.statistic-field-remove {
  flex: 0 0 auto;
  color: var(--text-muted, #a0aec0);
  cursor: pointer;

  &:hover {
    color: #eb554e;
  }
}

.estimated-stat-note {
  margin-top: 0.35em;
  font-size: 0.8em;
  color: rgba(0, 0, 0, 0.45);
  cursor: help;
}

// Phones / narrow mobile web (wave convention: desktop web byte-identical).
// The save (floppy) and remove (x) icons were ~12x20 touch targets crammed
// into a one-wide column.
@media only screen and (max-width: 768px) {
  .ui.inline.fields > .one.wide.field {
    display: flex;
    align-items: center;
    gap: 2px;
    width: auto !important;

    > a,
    > span {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 40px;
      min-height: 40px;
      margin: 0;
    }
  }
}
</style>
